import React, {useEffect} from 'react';
import {Typography} from '@mui/material';
import ProjectHeader from '../components/project/ProjectHeader';
import ProjectTextBox from '../components/project/ProjectTextBox';
import ProjectImage from '../components/project/ProjectImage';
import ProjectTwoColumns from '../components/project/ProjectTwoColumns';
import ProjectPersona from '../components/project/ProjectPersona';
import ProjectQuestions from '../components/project/ProjectQuestions';
import Footer from '../components/Footer';

function RMM() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div>
            <ProjectHeader
                backgroundImage="/assets/images/cover-rmm.png"
                projectName="Remember My Medicines"
                role="UX/UI Designer"
                duration="Sep – Oct 2021"
            />

            <ProjectTwoColumns>
                <div>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>Project Vision</Typography>
                    <Typography variant="body1">
                        <strong>Remember My Medicines</strong> is a cross-platform tool that helps young adults to remember to take medicines on time. The typical user is between 18–65 years old, and most users are people that need to take their meds on time.
                    </Typography>
                </div>
                <div>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>Challenges</Typography>
                    <Typography variant="body1">
                        Create a mobile app that is user-friendly by providing clear navigation and offering a fast process for reminders with notification.
                    </Typography>
                </div>
            </ProjectTwoColumns>

            <ProjectImage url="/assets/images/DesktopMobile.RMM.png" alt="Remember My Medicines desktop and mobile views"/>

            <ProjectTextBox withBackground>
                <Typography variant="h5" fontWeight="bold">Kickoff</Typography>
                <Typography variant="body1">
                    In this project, I conducted <strong>user interviews</strong> which I then turned into empathy maps to better understand the target user and their needs. I have found that some users struggle to take periodic medications when they need them because they have more commitments during the day or for their distractions. However, many mobile apps that remember the users to take medicine are confusing to navigate, creating inconvenience for users. I have tried to improve their user experience and make them cross-platform.
                </Typography>
                <ProjectQuestions questions={[
                    'What is the product and who is it for?',
                    'What do our users need most?',
                    'What challenges could we face moving forward?',
                    'Who do we see as our biggest competitors?',
                ]}/>
            </ProjectTextBox>

            <ProjectTextBox>
                <Typography variant="h5" fontWeight="bold">Affinity Diagram</Typography>
                <Typography variant="body1">
                    I used an <strong>affinity diagram</strong> to separate the data into groups of tasks which were further categorized by high-level goals for improvement in efficiency, process, depth, and familiarity. Recognizing the conflicts of interest from each audience allowed me to focus on shaping user goals.
                </Typography>
                <ProjectImage url="/assets/images/affinity diagram.rmm.png" alt="Affinity diagram"/>
            </ProjectTextBox>

            <ProjectTwoColumns withBackground title="Users">
                <div>
                    <ProjectPersona url="/assets/images/meredith.png" name="Primary">
                        <li><strong>Name:</strong> Meredith</li>
                        <li><strong>Age:</strong> 23</li>
                        <li><strong>Occupation:</strong> Customer service representative</li>
                    </ProjectPersona>
                    <Typography variant="body1">
                        She is a working student who needs to take her meds on time because she won't have any health problems.
                    </Typography>
                </div>
                <div>
                    <ProjectPersona url="/assets/images/olga.png" name="Secondary">
                        <li><strong>Name:</strong> Olga</li>
                        <li><strong>Age:</strong> 42</li>
                        <li><strong>Occupation:</strong> Business consultant</li>
                    </ProjectPersona>
                    <Typography variant="body1">
                        She is a business consultant who needs a way to remind her daughter to take medicine when she needs it because being a little disorderly. Olga would like to make her more autonomous.
                    </Typography>
                </div>
            </ProjectTwoColumns>

            <ProjectTextBox>
                <Typography variant="h5" fontWeight="bold">Competitive Analysis</Typography>
                <Typography variant="body1">
                    I created a <strong>competitive audit</strong> examining several potential competitors and I found out none have a cross-platform app. The majority of the features between competitors were very similar, the main differences that I noticed were:
                </Typography>
                <Typography variant="body1">~ Easily Accessible vs Hardly Accessible</Typography>
                <Typography variant="body1">~ Too Many Steps vs Simplified Interaction</Typography>
                <Typography variant="body1">~ Distracting Interface vs Minimalistic Interface</Typography>
                <ProjectImage url="/assets/images/audit.rmm.png" alt="Competitive audit"/>
            </ProjectTextBox>

            <ProjectTextBox withBackground>
                <Typography variant="h5" fontWeight="bold">Sitemap</Typography>
                <Typography variant="body1">
                    I've built a <strong>user flow</strong> of what a basic journey looks like from start to finish. This helps me understand the ways that users can interact with the product, as well as allowing me to see navigation through the user's goals.
                </Typography>
                <ProjectImage url="/assets/images/journeymap.rmm.png" alt="Journey map"/>
            </ProjectTextBox>

            <ProjectTextBox>
                <Typography variant="h5" fontWeight="bold">Wireframe</Typography>
                <Typography variant="body1">
                    After sketching out some <strong>paper wireframes</strong> and thinking through the preliminary flow, I reviewed what was necessary or unnecessary and what areas needed improvement. I poured a lot of time into this step to make sure I had the finishing touches before moving on to the visuals.
                </Typography>
            </ProjectTextBox>

            <ProjectImage url="/assets/images/wireframe-rmm.png" alt="Wireframes"/>

            <ProjectTextBox withBackground>
                <Typography variant="h5" fontWeight="bold">Interaction</Typography>
                <Typography variant="body1">
                    After creating my prototype from low fidelity wireframes, I asked 5 participants to run through different scenarios in my prototype in hope of garnering enough <strong>feedback</strong> to use for my next set of design interaction.
                </Typography>
            </ProjectTextBox>

            <ProjectTextBox>
                <Typography variant="h5" fontWeight="bold">Mockups</Typography>
                <Typography variant="body1">
                    Based on insights from usability studies, I applied design changes such as easily creating a reminder and sharing it with whoever the user wants. Additional design changes included accessibility to medicines monitoring over time.
                </Typography>
            </ProjectTextBox>

            <ProjectImage url="/assets/images/mockup.rmm.png" alt="Mockups"/>

            <ProjectTextBox withBackground>
                <Typography variant="h5" fontWeight="bold">Design System</Typography>
                <ProjectImage url="/assets/images/ds.rmm.png" alt="Design system"/>
            </ProjectTextBox>

            <ProjectImage url="/assets/images/secondmockap.rmm.png" alt="Additional mockups"/>

            <ProjectTextBox>
                <Typography variant="h5" fontWeight="bold">Takeaways</Typography>
                <Typography variant="body1">
                    I created this app during my course Google UX Designer on Coursera. I learned that even a small design change can have a huge impact on the user experience. The most important takeaway for me is to always focus on the real needs of the user when coming up with design ideas and solutions.
                </Typography>
            </ProjectTextBox>

            <Footer/>
        </div>
    );
}

export default RMM;
